import React, { useEffect, useState } from 'react'

import FormBlock from 'components/Forms/FormBlock'
import FormModal from 'components/Modal/FormModal/FormModal'
import Toast, { TOAST_TYPE } from 'components/Toast/Toast'
import Input from 'components/Forms/Input/Input'

import { fieldsValidator, getIsIntStringRule, getIsNotEmptyStringRule, getIsValueRule } from 'utils/fieldsValidator'
import { removeKeyFromObject } from 'utils/common'
import { getMonthlyPrice, getYearlyFormattedPrice } from 'utils/payments'

import styles from './UpdateInflatedPriceModal.module.scss'

const PriceNote = ({ price, currency, billingCycle }) => {
    if (billingCycle !== 12) return null

    return (
        <div className={styles.priceNote}>
            <span className={styles.price}>{getYearlyFormattedPrice(price, currency, 1)}</span> / year
        </div>
    )
}

const UpdateInflatedPriceModal = ({ currenciesSymbol, payload: { product, currency }, onSubmit, onClose }) => {
    const [price, setPrice] = useState('')
    const [initialInflatedPrice, setInitialInflatedPrice] = useState('')
    const [newInflatedPrice, setNewInflatedPrice] = useState('')

    const [errors, setErrors] = useState({})

    useEffect(() => {
        const inflatedPrice = product.inflatedPrice?.[currency] || null
        if (inflatedPrice) setInitialInflatedPrice(getMonthlyPrice(inflatedPrice, product.billingCycleValue))

        setPrice(getMonthlyPrice(product.multiCurrencyPrice[currency], product.billingCycleValue))
    }, [])

    useEffect(() => {
        setNewInflatedPrice(initialInflatedPrice)
    }, [initialInflatedPrice])

    const handleSubmit = async () => {
        try {
            setErrors({})

            const validator = new fieldsValidator()

            validator.addFields([
                validator.createField('newInflatedPrice', String(newInflatedPrice), [
                    getIsValueRule(),
                    getIsNotEmptyStringRule(),
                    getIsIntStringRule(),
                ]),
            ])

            const { isHasErrors, errors } = validator.validate()
            if (isHasErrors) {
                setErrors(errors)
                return
            }

            const price = product.billingCycleValue === 12 ? newInflatedPrice * 12 : newInflatedPrice
            await onSubmit({ product, currency, price, isInflated: true })

            return true
        } catch (err) {
            console.error(err)
            Toast(TOAST_TYPE.ERROR)
            throw err
        }
    }

    return (
        <FormModal
            headText="Update Inflated Price"
            subHeadText={`${product.name} (${product.productCode})`}
            successText="Inflated price updated successfully!"
            actionText="Update"
            onAction={() => handleSubmit()}
            onClose={() => onClose()}
            isDisabledAction={String(newInflatedPrice) === String(initialInflatedPrice)}
        >
            <FormBlock label={`Price (${currenciesSymbol[currency]} / Month)`}>
                <PriceNote price={price} billingCycle={product.billingCycleValue} currency={currency} />
                <Input value={price} isDisabled isReadOnly />
            </FormBlock>
            <FormBlock label={`Old Inflated Price (${currenciesSymbol[currency]} / Month)`}>
                <PriceNote price={initialInflatedPrice} billingCycle={product.billingCycleValue} currency={currency} />
                <Input value={initialInflatedPrice} isDisabled isReadOnly />
            </FormBlock>
            <FormBlock label={`New Inflated Price (${currenciesSymbol[currency]} / Month)`}>
                <PriceNote price={newInflatedPrice} billingCycle={product.billingCycleValue} currency={currency} />
                <Input
                    value={newInflatedPrice}
                    type="number"
                    min={1}
                    error={errors.newInflatedPrice}
                    onChange={value => {
                        setNewInflatedPrice(value)
                        setErrors(removeKeyFromObject(errors, 'newInflatedPrice'))
                    }}
                />
            </FormBlock>
        </FormModal>
    )
}

export default UpdateInflatedPriceModal
